import { call, post, patch } from "@/services/api";

export type Warehouse = { id: number; name: string };
export type ReturnEvent = {
  id: number; title: string | null; client_name: string | null; contract_id: number | null;
  contract_name: string | null; kind: string; type_label: string | null; starts_at: string | null;
  warehouse: Warehouse | null; approved: boolean; entered: number;
};
export type ReturnItem = {
  product_id: number; name: string | null; sku: string | null; image_url: string | null;
  product_type_slug: string | null; rooms: string[]; expected: number; entered: number;
  // His grid's type name, from the catalogue.
  type_name?: string | null;
  // How many parts the product is stored in (his composition badge); 1 when single or unknown.
  parts?: number;
  // His "2 pieces ?": only suggested from the catalogue, nobody has saved it yet.
  parts_suggested?: boolean;
};
export type CatalogueProduct = {
  id: number; sku: string | null; name: string | null; size: string | null; image_url: string | null;
  type: { id: number | null; slug: string | null; name: string | null };
};
export type Condition = "new" | "good" | "light_marks" | "damaged";
export type StockPiece = {
  id: number; reference: string; status: "in_stock" | "reserved" | "left"; source: "return" | "reception";
  myotaku_warehouse_id: number; myotaku_product_id: number | null;
  product: { name: string | null; sku: string | null; size: string | null; image_url: string | null;
             type: { name: string | null } | null; public_url?: string | null } | null;
  piece_names: string[];
  // His record's `retour` and `product` as they were at intake.
  return_snapshot?: { title?: string; client?: string; date?: string; type?: string } | null;
  product_snapshot?: { supplier?: string; type?: string; size?: string; url?: string; arrival_on?: string } | null;
  // His `order.oid`: the supplier order a labelled reception piece came from.
  myotaku_order_id?: number | null;
  condition: Condition; damage_note: string | null;
  wrapped_in_blanket: boolean; wrapped_in_film: boolean; pallet: string | null; rack: string | null;
  myotaku_return_event_id: number | null; myotaku_contract_id: number | null;
  // A reception piece labelled for a client delivery: which delivery, which contract line, piece k of n.
  myotaku_delivery_event_id?: number | null; myotaku_contract_item_id?: number | null;
  unit_number?: number | null; unit_count?: number | null;
  received_by: string | null; received_at: string; labelled_at: string | null; left_at: string | null;
  days_in_stock: number;
  // His inventory row: the warehouse (all-warehouses view) and the client a piece is reserved for.
  warehouse_name?: string; reserved_for?: { client: string | null; delivery_on: string | null; number?: number | null; owner?: string | null } | null;
  // The piece's own identification photo (else its first) and whether it has damage photos.
  photo?: { url: string | null; thumb_url: string | null } | null; has_damage_photos?: boolean;
  events?: { action: string; value: string | null; by_name: string | null; at: string }[];
  // His detail statistics (the piece's own page only).
  stats?: PieceStats;
};
export type PieceStats = {
  in_stock: boolean; stay_days: number; stay_since: string | null; back: boolean; out_on: string | null; total_days: number;
  ins: number; outs: Record<string, number>; last_movement: { action: string; at: string } | null; place_since: string | null;
  same_here: number; same_all: number; same_out: number; same_avg_days: number; same_count: number; same_from_returns: number;
  // His timeline bar: each stay [from, to]; an open one ends now.
  stays?: [string, string][];
};


const q = (w?: number | null) => (w ? `?warehouse_id=${w}` : "");

export const fetchReturns = (w?: number | null) =>
  call<{ warehouses: Warehouse[]; returns: ReturnEvent[] }>(`/api/v1/stock/returns${q(w)}`);
// His card tag's second number, apart from the list so the list never waits for it.
export const fetchReturnsExpected = (ids: number[]) =>
  call<{ expected: Record<string, number | null> }>(`/api/v1/stock/returns/expected?ids=${ids.join(",")}`);
export const fetchReturnItems = (eventId: number) =>
  call<{ event: { id: number; client_name: string | null; title: string | null }; items: ReturnItem[] }>(`/api/v1/stock/returns/${eventId}`);
export const searchCatalogue = (text: string) =>
  call<{ products: CatalogueProduct[]; unavailable: boolean }>(`/api/v1/stock/catalogue?q=${encodeURIComponent(text)}`);
export const fetchPieces = (w?: number | null, status?: string) =>
  call<{ pieces: StockPiece[] }>(`/api/v1/stock/pieces${q(w)}${status ? `${w ? "&" : "?"}status=${status}` : ""}`);
export const fetchPiece = (id: number) => call<StockPiece>(`/api/v1/stock/pieces/${id}`);
export const createPieces = (b: Record<string, unknown>) => post<{ pieces: StockPiece[] }>(`/api/v1/stock/pieces`, b);
export const updatePiece = (id: number, b: Record<string, unknown>) => patch<StockPiece>(`/api/v1/stock/pieces/${id}`, b);
export const labelPiece = (id: number) => post<StockPiece>(`/api/v1/stock/pieces/${id}/label`);
export const leavePiece = (id: number, reason: string) => post<StockPiece>(`/api/v1/stock/pieces/${id}/leave`, { reason });
export const backPiece = (id: number) => post<StockPiece>(`/api/v1/stock/pieces/${id}/back`);
export const deletePiece = (id: number) => call<{ deleted: boolean }>(`/api/v1/stock/pieces/${id}`, { method: "DELETE" });
// His OUT_REASONS.
export const OUT_REASONS = ["redeliv", "repair", "sale", "scrap", "transfer"] as const;

// His Stock › Étiquetage des réceptions.
// His labelling list: the assembly jobs (his Montage data) of the warehouse, by delivery date.
export type LabellingDelivery = {
  event_id: number; number: number | string | null; client_name: string | null; contract_name: string | null; contract_id: number | null;
  delivery_on: string | null; day: string | null; warehouse: Warehouse | null; owner: string | null; run_status: string | null; labelled: number;
};
// One supplier-order line of a job: one physical piece to label.
export type LabellingUnit = {
  row: number; line_id: number; name: string | null; sku: string | null; product_id: number | null; product_type_slug: string | null;
  image_url: string | null; order_id: number | null; supplier: string | null; arrival_on: string | null; assembled_qty: number;
  unit_number: number; unit_count: number; piece_id: number | null;
};
export type LabellingTotals = { total: number; labelled: number; assembled: number } | null;
export type SupplierOrderHint = { order_id: number | null; supplier: string | null; arrival_on: string | null; client: string | null };
export const fetchLabelling = (w?: number | null) =>
  call<{ deliveries: LabellingDelivery[] }>(`/api/v1/stock/labelling${q(w)}`);
export const fetchLabellingTotals = (ids: number[]) =>
  call<{ totals: Record<string, LabellingTotals> }>(`/api/v1/stock/labelling/totals?ids=${ids.join(",")}`);
export const fetchLabellingUnits = (eventId: number) =>
  call<{ event: { client_name: string | null; delivery_on: string | null; number: number | null; owner: string | null }; units: LabellingUnit[] }>(`/api/v1/stock/labelling/${eventId}`);
export const labelReception = (eventId: number, row?: number) =>
  post<{ pieces: StockPiece[] }>(`/api/v1/stock/labelling/${eventId}/label`, row ? { row } : {});
// His order hint: the supplier orders of this warehouse's jobs that carry a product.
export const fetchOrdersFor = (productId: number | null, sku: string | null, w?: number | null) =>
  call<{ orders: SupplierOrderHint[] }>(`/api/v1/stock/orders_for?${new URLSearchParams({ ...(productId ? { product_id: String(productId) } : {}), ...(sku ? { sku } : {}), ...(w ? { warehouse_id: String(w) } : {}) })}`);

// "Reconnaître avec Claude": a photo of the returned piece, matched to its product on the worker.
export type RecognitionCandidate = {
  product_id: number; name: string | null; sku: string | null; type: string | null;
  image_url: string | null; remaining?: number; src?: "retour" | "ai";
  // His card's facts: the return's rooms, and from the catalogue the supplier, type, size, description
  // and page ("not in online catalogue" when it has none).
  rooms?: string[]; size?: string | null; description?: string | null; supplier?: string | null;
  type_name?: string | null; public_url?: string | null; in_catalogue?: boolean;
};
export type Recognition = {
  id: number; status: "new" | "pending" | "running" | "done" | "failed" | "cancelled"; scope: "retour" | "catalogue";
  error: string | null; photo_url: string | null;
  // His progress step, and which of his three failure messages applies.
  phase?: "type" | "retour" | "catalogue" | null; error_kind?: "unavailable" | "rate_limited" | "failed" | null;
  result: null | {
    top: RecognitionCandidate | null; alts: RecognitionCandidate[]; confidence: number; seen: string; reason: string;
    source: "retour" | "catalogue"; retour_checked: boolean; catalogue_offered: boolean;
  };
};
export const createRecognition = (b: { return_event_id: number | null; locale: string }) =>
  post<Recognition>(`/api/v1/stock/recognitions`, b);
export const fetchRecognition = (id: number) => call<Recognition>(`/api/v1/stock/recognitions/${id}`);
export const runRecognition = (id: number, scope?: "catalogue", returnEventId?: number | null) =>
  post<Recognition>(`/api/v1/stock/recognitions/${id}/run`, { ...(scope ? { scope } : {}), ...(returnEventId !== undefined ? { return_event_id: returnEventId } : {}) });
export const cancelRecognition = (id: number) => post<Recognition>(`/api/v1/stock/recognitions/${id}/cancel`, {});
